import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { useHistory } from 'react-router';
import Registration from '../../components/Registration/Registration';
import { ReactComponent as PhoneLogo } from '../../assets/images/svg/phone.svg';
import Logo from '../../components/Logo/Logo';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import {
  mediaBelow,
  mediaBetween,
  mediaUp,
  pxToRem,
} from '../../assets/styles/helper';
import config from '../../config';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import LoaderIcon from '../../components/Loader/LoaderIcon';
import LoaderContainer from '../../components/Loader/LoaderContainer';
import LoaderWrap from '../../components/Loader/LoaderWrap';
import { EMAIL_LOGIN_PAGE } from '../../constants/pages';
import { useBrokerName } from '../../util/hooks/userBrokerName';
import RegistrationPageOne from '../../components/Registration/RegistrationPageOne';
import Paragraph from '../../components/Paragraph/Paragraph';
import Anchor from '../../components/Anchor/Anchor';
import RegistrationPageTwo from '../../components/Registration/RegistrationPageTwo';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import { ReactComponent as ChevronLeft } from '../../assets/images/svg/chevron-left.svg';
import RegistrationSidebarWithPrompt from './RegistrationSidebarWithPrompt';
import { removeFromSessionStorage } from '../../util/helpers/sessionStorageHelper';

const getSidebarBackground = () => {
  const { brokerTheme } = config;

  switch (brokerTheme) {
    case 'GoldSilver':
    case 'Contoso':
    case 'LegacyPM':
    default:
  }
};

const RegistrationBody = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '0.5fr 1fr')};
  min-height: ${({ isMobile, footerHeight }) =>
    isMobile ? 'auto' : `calc(100vh - ${footerHeight}px - 2rem)`};
  overflow: visible;
  margin: ${({ isMobile }) => (isMobile ? '' : '1rem')};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    ${({ isGS }) => (isGS ? `` : `margin-bottom: -4rem;`)};
  }
  ${mediaBelow(variables.breakpoints.bpLg)} {
    ${({ isGS }) => (isGS ? `` : `display: flex; flex-direction: column;`)};
  }
  ${mediaBetween(variables.breakpoints.bpLg, variables.breakpoints.bp1366)} {
    grid-template-columns: 40% 60%;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bpXxxl)} {
    grid-template-columns: 534px auto;
  }
  ${mediaBetween(variables.breakpoints.bpXxxl, variables.breakpoints.bp1600)} {
    grid-template-columns: 612px auto;
  }
  ${mediaBetween(variables.breakpoints.bp1600, variables.breakpoints.bp1920)} {
    grid-template-columns: 750px auto;
  }
  ${mediaUp(variables.breakpoints.bp1920)} {
    grid-template-columns: 850px auto;
  }
`;

const BannerWrapper = styled.div`
  display: flex;
  background: ${themeColors.colorPrimary};
  width: 100%;
  padding: ${variables.margins.fourteen};
`;

const RegistrationSidebar = styled.div`
  ${({ isGS }) =>
    isGS
      ? `background: ${themeColors.colorSidebarBegin};`
      : `background-image: url(${getSidebarBackground()});
    `};
  background-size: ${({ isGS }) => (isGS ? 'contain' : 'cover')};
  ${({ isGS }) =>
    isGS && `background-repeat: no-repeat; background-position: right bottom`};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;

  svg {
    color: ${themeColors.colorHeaderLogoLight};
    height: 1.75rem;
    object-fit: contain;
  }
`;

const RegistrationStepWrapper = styled.div`
  padding: 16px;
  width: 100%;
  max-width: 600px;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    margin: 0;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bp1600)} {
    max-width: 636px;
  }
  ${mediaUp(variables.breakpoints.bp1600)} {
    max-width: 650px;
  }
  ${mediaBetween(variables.breakpoints.bpLg, variables.breakpoints.bpXl)} {
    width: auto;
  }
`;

const RegistrationContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${mediaBelow(variables.breakpoints.bpLg)} {
    align-items: start;
  }
`;

const RegistrationTitle = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: normal;
  color: ${themeColors.colorTextPrimary};
  margin-top: 0px;
  text-align: left;
  font-family: ${variables.family.goldSilver};
  ${mediaBelow(variables.breakpoints.bpXxxl)} {
    font-size: 24px;
  }
  ${mediaBelow(variables.breakpoints.bpLg)} {
    line-height: 26px;
    margin-bottom: 10px;
  }
`;

const RegistrationSubtitle = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 16px;
  line-height: 1.6;
  color: ${themeColors.colorText};
  font-weight: 600;
  margin-bottom: 0;
  line-break: loose;

  ${mediaBelow(variables.breakpoints.bpXs)} {
    font-size: 14px;
  }
`;

const PhoneIcon = styled(PhoneLogo)`
  width: 24px;
  height: 24px;
  margin-right: ${variables.margins.eight};
  flex-shrink: 0;
`;

const PhoneWrapper = styled.span`
  font-weight: bold;
  white-space: nowrap;
`;

const BackNavigatorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-left: ${({ marginLeft }) => (marginLeft ? pxToRem(marginLeft) : '')};
  margin-top: ${({ marginTop }) => (marginTop ? pxToRem(marginTop) : '')};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? pxToRem(marginBottom) : pxToRem(30)};
  margin-right: ${({ marginRight }) =>
    marginRight ? pxToRem(marginRight) : ''};
  ${mediaBelow(variables.breakpoints.bpLg)} {
    padding: 16px;
    margin: 0;
  }
`;

const ChevronIconWrapper = styled.div`
  display: flex;
  margin-right: ${pxToRem(10)};
  cursor: pointer;
`;

const StepHeader = styled.div`
  display: flex;
  padding: 16px 0;
`;

const MAX_STEPS = 2;
const FIRST_STEP = 1;
const STEP_INCREMENT = 1;

const TwoStepQuickRegistration = () => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const [registrationUrl, setRegistrationUrl] = useState('');
  const history = useHistory();
  // Capture whether the registration was opened in a new tab (no prior history)
  const [openedInNewTab] = useState(() => window.history.length === 1);
  const brokerName = useBrokerName();
  const [stepNumber, setStepNumber] = useState(FIRST_STEP);
  // const [showConfirmation, setShowConfirmation] = useState(false);
  const windowSize = useWindowSize();
  const isMobile =
    (windowSize.width ?? window.innerWidth) < variables.breakpoints.bpLg;
  const [isOpen, setIsOpen] = useState(false);
  const [supportPhoneNumber, setSupportPhoneNumber] = useState('');
  const [footerHeight, setFooterHeight] = useState(0);

  useLayoutEffect(() => {
    removeFromSessionStorage('twostep1');
    removeFromSessionStorage('twostep2');

    const handleBeforeUnload = () => {
      removeFromSessionStorage('twostep1');
      removeFromSessionStorage('twostep2');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeFromSessionStorage('twostep1');
      removeFromSessionStorage('twostep2');
    };
  }, []);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return;
    footer.style.display = isOpen ? 'none' : '';
  }, [isOpen]);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(entry.target.getBoundingClientRect().height);
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return undefined;
    if (!isMobile) {
      footer.style.position = 'fixed';
      footer.style.bottom = '0px';
    } else {
      footer.style.position = 'static';
      footer.style.bottom = 'auto';
    }
    return () => {
      footer.style.position = '';
      footer.style.bottom = '';
    };
  }, [isMobile]);

  useEffect(() => {
    if (!settings?.SupportPhone) {
      return;
    }

    setSupportPhoneNumber(settings.SupportPhone);
  }, [settings.SupportPhone]);

  const BackNavigator = () => (
    <>
      <BackNavigatorWrapper
        marginLeft={16}
        marginTop={8}
        marginBottom={8}
        onClick={() => setIsOpen(true)}
      >
        <ChevronIconWrapper>
          <ChevronLeft
            height={24}
            marginLeft="50px"
            stroke={themeColors.colorNavIcon}
          />
        </ChevronIconWrapper>
        <Paragraph bold fontSize={16} usePx>
          {t('quickRegistration.twoStep.rightPane.createAccount')}
        </Paragraph>
      </BackNavigatorWrapper>
    </>
  );

  // Ref so history.listen always reads the latest stepNumber without stale closure
  const stepNumberRef = useRef(stepNumber);
  useEffect(() => {
    stepNumberRef.current = stepNumber;
  });

  // Build back-button guard stack on mount.
  // Guards use same-URL pushes so the component never unmounts (same route match).
  // Guard identity is embedded in history entry state so history.listen knows
  // exactly where the user popped to — no dependency on async ref updates.
  //
  //   Desktop: [base(_rgBase), step1(current)]
  //   Mobile:  [base(_rgBase), testimonial(_rgTestimonial), step1(current)]
  //
  // Works even when the page is opened directly with no prior browser history.
  useLayoutEffect(() => {
    const registrationPath =
      history.location.pathname + (history.location.search || '');
    const isMobileDevice = window.innerWidth < variables.breakpoints.bpLg;

    // Tag the initial entry as base guard — same URL, no remount, no unmount
    history.replace(registrationPath, { rgBase: true });

    if (isMobileDevice) {
      // Testimonial guard — tagged so listener shows testimonial on pop
      history.push(registrationPath, { rgTestimonial: true });
    }
    // Step1 guard — current position (untagged)
    history.push(registrationPath);

    const unlisten = history.listen((location, action) => {
      if (action !== 'POP') return;

      if (stepNumberRef.current === MAX_STEPS) {
        // Back from step 2 → step 1
        setStepNumber(FIRST_STEP);
      } else if (isMobileDevice && location.state?.rgTestimonial) {
        // Popped to testimonial guard → show testimonial
        if (stepNumberRef.current === 1 && !isOpen) {
          history.replace(EMAIL_LOGIN_PAGE);
        } else {
          setIsOpen(true);
        }
      } else {
        // Popped to base guard (desktop step1 or mobile testimonial) → login-email
        history.replace(EMAIL_LOGIN_PAGE);
      }
    });

    return unlisten;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStep = (currentStep) => {
    if (
      typeof currentStep !== 'number' ||
      currentStep + STEP_INCREMENT > MAX_STEPS
    ) {
      return FIRST_STEP;
    }
    setStepNumber(currentStep + 1);
    // Push a history entry so browser back from step 2 triggers the popstate guard
    history.push(history.location.pathname + history.location.search);
  };

  const backStep = (currentStep) => {
    if (typeof currentStep !== 'number') return;
    if (currentStep > FIRST_STEP) {
      setStepNumber(currentStep - STEP_INCREMENT);
      return;
    }
    // Step 1 back (chevron click)
    if (isMobile) {
      setIsOpen(true); // Show USP screen
    } else if (!openedInNewTab) {
      history.goBack(); // Desktop same tab → previous page
    }
    // Desktop new tab: chevron is hidden — this branch is never reached
  };

  useEffect(() => {
    if (!settings || isEmpty(settings)) {
      return;
    }
    const { BrokerCode, IsQuickRegistrationActive } = settings;
    if (BrokerCode && !IsQuickRegistrationActive) {
      const regUrl = settings?.RegistrationUrlV2;
      setRegistrationUrl(regUrl);
    }
  }, [settings]);

  // const goStepBack = () => {
  //   history.push(EMAIL_LOGIN_PAGE);
  // };

  if (registrationUrl && registrationUrl.length > 0) {
    window.location.replace(registrationUrl);
  }

  const LeftPane = () => {
    switch (brokerName) {
      case 'GoldSilver':
        if (isMobile) {
          if (isOpen) {
            return (
              <RegistrationSidebarWithPrompt
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              >
                <Logo />
              </RegistrationSidebarWithPrompt>
            );
          }
          return <></>;
        }
        return (
          <RegistrationSidebarWithPrompt isOpen={isOpen} setIsOpen={setIsOpen}>
            <Logo />
          </RegistrationSidebarWithPrompt>
        );

      case 'GBI Direct':
        return (
          <RegistrationSidebar isGS={config?.brokerTheme === 'GoldSilver'}>
            <Logo />
          </RegistrationSidebar>
        );
      default:
        return <></>;
    }
  };

  const Banner = () => (
    <BannerWrapper>
      <Logo />
    </BannerWrapper>
  );

  const renderQuickRegistrationPage = () => {
    if (brokerName === 'GoldSilver') {
      if (stepNumber === FIRST_STEP) {
        return (
          <RegistrationPageOne
            stepNumber={stepNumber}
            updateStep={updateStep}
            isOpen={isOpen}
          />
        );
      }
      return (
        <RegistrationPageTwo stepNumber={stepNumber} updateStep={updateStep} />
      );
    }
    return <Registration />;
  };

  if (
    !settings ||
    isEmpty(settings) ||
    settings?.BrokerName === undefined ||
    settings?.IsQuickRegistrationActive === undefined
  ) {
    return (
      <LoaderContainer>
        <LoaderWrap data-cy="loading-indicator">
          <LoaderIcon />
        </LoaderWrap>
      </LoaderContainer>
    );
  }

  if (isOpen && isMobile) {
    return (
      <RegistrationBody
        isMobile={isMobile}
        isGS={brokerName === 'GoldSilver'}
        footerHeight={footerHeight}
      >
        <RegistrationSidebarWithPrompt isOpen={isOpen} setIsOpen={setIsOpen}>
          <Logo />
        </RegistrationSidebarWithPrompt>
      </RegistrationBody>
    );
  }

  return (
    <>
      {brokerName === 'GoldSilver' && isMobile && <BackNavigator />}
      {brokerName === 'GoldSilver' && isMobile && <Banner />}
      <RegistrationBody
        isMobile={isMobile}
        isGS={brokerName === 'GoldSilver'}
        footerHeight={footerHeight}
      >
        <LeftPane />
        <RegistrationContent>
          <RegistrationStepWrapper footerHeight={footerHeight}>
            <RegistrationTitle>
              {t('quickRegistration.twoStep.rightPane.title')}
            </RegistrationTitle>
            <RegistrationSubtitle>
              <PhoneIcon />
              {t('quickRegistration.twoStep.rightPane.subtitle', {
                brokerName,
              })}
              <PhoneWrapper>
                &nbsp;
                <Anchor
                  type="telephone"
                  value={settings?.SupportPhoneTollFree}
                  text={supportPhoneNumber}
                  bold
                />
              </PhoneWrapper>
            </RegistrationSubtitle>
            <StepHeader>
              {stepNumber === MAX_STEPS && (
                <ChevronIconWrapper onClick={() => backStep(stepNumber)}>
                  <ChevronLeft height={24} stroke={themeColors.colorPrimary} />
                </ChevronIconWrapper>
              )}
              <Paragraph
                bold
                color={themeColors.colorPrimary}
                fontSize={18}
                usePx
              >
                {t('quickRegistration.twoStep.rightPane.stepNumber', {
                  stepNumber,
                })}
              </Paragraph>
            </StepHeader>
            {renderQuickRegistrationPage()}
          </RegistrationStepWrapper>
        </RegistrationContent>
      </RegistrationBody>
    </>
  );
};

TwoStepQuickRegistration.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};
export default TwoStepQuickRegistration;
